/**
 * useAnnouncements Hook (admin)
 *
 * CRUD over the `announcements` collection that powers the site-wide banner.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuth } from '@/contexts';

export type AnnouncementType = 'info' | 'promo' | 'warning';

export interface Announcement {
  id: string;
  message: string;
  type: AnnouncementType;
  active: boolean;
  ctaLabel?: string;
  ctaUrl?: string;
  createdAt: Date;
}

export interface NewAnnouncement {
  message: string;
  type: AnnouncementType;
  active: boolean;
  ctaLabel?: string;
  ctaUrl?: string;
}

const toDate = (val: unknown): Date =>
  val instanceof Timestamp ? val.toDate() : new Date();

export const useAnnouncements = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, 'announcements'), orderBy('createdAt', 'desc'))
        );
        if (cancelled) return;
        setAnnouncements(
          snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              message: data.message || '',
              type: (data.type as AnnouncementType) || 'info',
              active: data.active === true,
              ctaLabel: data.ctaLabel || undefined,
              ctaUrl: data.ctaUrl || undefined,
              createdAt: toDate(data.createdAt),
            };
          })
        );
      } catch (err) {
        console.error('Failed to fetch announcements:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchKey]);

  const create = useCallback(
    async (data: NewAnnouncement) => {
      await addDoc(collection(db, 'announcements'), {
        ...data,
        ctaLabel: data.ctaLabel || null,
        ctaUrl: data.ctaUrl || null,
        createdBy: user?.uid || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      refetch();
    },
    [user, refetch]
  );

  const toggleActive = useCallback(
    async (id: string, active: boolean) => {
      await updateDoc(doc(db, 'announcements', id), {
        active,
        updatedAt: serverTimestamp(),
      });
      refetch();
    },
    [refetch]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteDoc(doc(db, 'announcements', id));
      refetch();
    },
    [refetch]
  );

  return { announcements, isLoading, create, toggleActive, remove, refetch };
};

export default useAnnouncements;
